import { GET_RANKING_BY_TEAM } from 'constants/api'
import dayjs from 'dayjs'
import axios from 'axios'
import { useSession } from 'contexts/session-context'
import { useEffect, useState } from 'react'
import { RankingGroup } from 'contexts/groups-context/groups-reducer'
import textServices from 'services/text-services'

interface IProps {
  toggleRanking: () => void
  team: any
}

const Header = ({ toggleRanking }: any) => {
  return (
    <div className="w-full referrals__ranking__header">
      <div className="referrals__ranking__header__title">
        <button onClick={toggleRanking}>
          <img
            src={`${AZURE_BASE_URL}/referrals-arrow.webp`}
            alt="Arrow"
            className="referrals__ranking__header__title__arrow"
          />
        </button>
        <h2>
          {textServices.findByText('referrals/rank/title', {
            es: 'Ranking referidos',
            en: 'Referrals ranking'
          })}
        </h2>
      </div>

      <button className="referrals__ranking__header__download-btn" onClick={toggleRanking}>
        <img src={`${AZURE_BASE_URL}/referrals-back-arrow.webp`} alt="Ranking" />
        <p>
          {textServices.findByText('referrals/rank/button', {
            es: 'Referir',
            en: 'Referral'
          })}
        </p>
      </button>
    </div>
  )
}

const Status = ({ team }: any) => {
  return (
    <div className="referrals__ranking__status">
      <div className="referrals__ranking__status__container">
        <p className="active">ACTIVO</p>
      </div>
      <p className="referrals__ranking__status__text">Finaliza el {dayjs(team?.team?.validTo).format("DD/MM/YYYY")}</p>
    </div>
  )
}

const RankingItem = ({ position, name, code, score }: any) => {
  const backgroundClass = position <= 3 ? `referrals__ranking__item__${position}` : 'referrals__ranking__item__default'

  return (
    <div className={`referrals__ranking__item ${backgroundClass}`}>
      <div className="referrals__ranking__item__info">
        <div className="referrals__ranking__item__info__position">
          <p className="referrals__ranking__item__info__position__number">{position}</p>
        </div>

        <div className="referrals__ranking__item__info__position__texts">
          <p className="referrals__ranking__item__info__position__texts__name">{name}</p>
          <p className="referrals__ranking__item__info__position__texts__code">Código {code}</p>
        </div>
      </div>

      <div className="referrals__ranking__item__count">
        <img src={`${AZURE_BASE_URL}/referrals-ranking-item-icon.webp`} alt="Icon referido" />
        <p className="referrals__ranking__item__count__number">{score}</p>
      </div>
    </div>
  )
}

const Ranking = ({ toggleRanking, team }: IProps) => {
  const [{ data: session }] = useSession()
  const [rankInfo, setRankInfo] = useState<{ profile: RankingGroup | null; ranking: RankingGroup[] }>({
    profile: null,
    ranking: []
  })

  const getRanking = async () => {
    try {
      if (!session || !team?.team?.id || !team?.profileCluster?.clusterId) return

      const headers = {
        Authorization: `Bearer ${(session as any)._tokenResponse?.idToken}`,
        'Ph-Api-Id': PH_API_ID ?? 1
      }

      const URL = GET_RANKING_BY_TEAM.replace(':teamId', `${team.team.id}`).replace(
        ':clusterId',
        `${team.profileCluster.clusterId}`
      )

      const {
        data: { profileRanking, ranking }
      } = await axios.get<{ profileRanking: RankingGroup; ranking: RankingGroup[] }>(URL, { headers })

      setRankInfo({ profile: profileRanking, ranking })
    } catch (error) {
      console.error('Error fetching ranking:', error)
    }
  }

  useEffect(() => {
    getRanking()
  }, [team])

  const Position = () => {
    return (
      <div className="referrals__ranking__position">
        <div className="referrals__ranking__position__container">
          {rankInfo?.profile?.position > 0 ? (
            <>
              <p className="referrals__ranking__position__text w-6/12">
                Estás en la posición <b>{rankInfo?.profile?.position}</b>
              </p>

              <svg width="2" height="34" viewBox="0 0 2 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 0.333496V33.6668" stroke="#027219" />
              </svg>

              <div className="referrals__ranking__position__count w-6/12">
                <img src={`${AZURE_BASE_URL}/referrals-count-icon.webp`} alt="Referral icon" />
                <p className="referrals__ranking__position__count__text">{rankInfo?.profile?.points}</p>
              </div>
            </>
          ) : (
            <p className="referrals__ranking__position__text">Aún no tienes referidos, no estás en ninguna posición.</p>
          )}
        </div>
      </div>
    )
  }

  const RankingItemMap = () => {
    return (
      <>
        {rankInfo.ranking.length > 0 ? (
          rankInfo.ranking
            .slice(0, 7)
            .map((item, index) => (
              <RankingItem
                key={index}
                position={item.position}
                name={item.name}
                code={item?.profileId}
                score={item.points}
              />
            ))
        ) : (
          <p className="referrals__ranking__not-available">
            {textServices.findByText('referrals/rank/not-available', {
              es: 'No hay ranking disponibles',
              en: 'No rankings available'
            })}
          </p>
        )}
      </>
    )
  }

  return (
    <div className="flex flex-col w-full">
      <Header toggleRanking={toggleRanking} />
      <Status team={team} />
      {textServices.findByText('referrals/ranking/subtitle', {
        es: (
          <div className="referrals__ranking__container__subtitle flex flex-col gap-1">
            <p className="referrals__ranking__subtitle">
              Este es el ranking de referidos, aquí verás a los demás participantes y en qué posición te encuentras.
            </p>
          </div>
        ),
        en: (
          <div className="referrals__ranking__container__subtitle flex flex-col gap-1">
            <p className="referrals__ranking__subtitle">
              This is the referral ranking. Here, you will see the other participants and your current position.
            </p>
          </div>
        )
      })}

      <Position />
      <div className="referrals__ranking__item__container mt-4">
        <RankingItemMap />
      </div>
    </div>
  )
}

export default Ranking
